import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MdDescription, MdDownload } from 'react-icons/md';
import API from 'api/api';
import Card from 'components/card/Card';
import DocumentList from 'components/documents/DocumentList';
import DocumentUploader from 'components/documents/DocumentUploader';
import PageShell from 'components/layout/PageShell';
import { useTranslation } from 'i18n/TranslationProvider';
import { LANGUAGES } from 'i18n/languages';

const buildPurchaseTermsEntityId = (languageCode) => `purchase_terms_${languageCode}`;

const AdminPurchaseTermsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeDocument, setActiveDocument] = useState(null);
  const [isFetchingDownload, setIsFetchingDownload] = useState(false);

  const selectedLanguageLabel = useMemo(() => {
    const match = LANGUAGES.find((item) => item.code === selectedLanguage);
    return match?.label || selectedLanguage.toUpperCase();
  }, [selectedLanguage]);

  const fetchActiveDocument = useCallback(async () => {
    setIsFetchingDownload(true);
    try {
      const loadActive = async () => {
        const response = await API.get('/documents/purchase-terms', {
          params: { lang: selectedLanguage },
        });
        return response?.data?.data || null;
      };

      try {
        const active = await loadActive();
        setActiveDocument(active);
      } catch (error) {
        const status = error?.response?.status;
        if (status !== 404) {
          throw error;
        }

        const pendingResponse = await API.get('/documents', {
          params: {
            entityType: 'user',
            entityId: buildPurchaseTermsEntityId(selectedLanguage),
            docType: 'purchase_terms',
            status: 'pending',
          },
        });
        const pendingDocuments = pendingResponse?.data?.data || [];

        if (pendingDocuments.length > 0) {
          await Promise.allSettled(
            pendingDocuments
              .map((doc) => doc?.id || doc?.documentId)
              .filter(Boolean)
              .map((id) => API.post(`/documents/${id}/confirm`)),
          );
        }

        try {
          const active = await loadActive();
          setActiveDocument(active);
        } catch {
          setActiveDocument(null);
        }
      }
    } catch (error) {
      setActiveDocument(null);
    } finally {
      setIsFetchingDownload(false);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    fetchActiveDocument();
  }, [fetchActiveDocument, refreshTrigger]);

  const handleDownload = () => {
    if (!activeDocument?.downloadUrl) {
      toast({
        title: t('admin.purchase_terms_unavailable_for_selected_language', {
          fallback: 'Purchase terms document is unavailable for the selected language.',
        }),
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    window.open(activeDocument.downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const handleUploaded = () => {
    setRefreshTrigger((value) => value + 1);
  };

  const entityId = buildPurchaseTermsEntityId(selectedLanguage);

  return (
    <PageShell>
      <Stack spacing={6}>
        <Card p={{ base: 4, md: 6 }}>
          <HStack spacing={3} mb={2}>
            <Icon as={MdDescription} boxSize={5} color="blue.500" />
            <Text fontWeight="700" fontSize="lg">
              {t('admin.purchase_terms_management', {
                fallback: 'Purchase Terms Management',
              })}
            </Text>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={5}>
            {t('admin.purchase_terms_management_subtitle', {
              fallback: 'Upload and maintain localized Purchase Terms PDFs used in partner order acceptance.',
            })}
          </Text>

          <Stack spacing={4}>
            <FormControl maxW={{ base: 'full', md: '320px' }}>
              <FormLabel>{t('admin.document_language', { fallback: 'Document language' })}</FormLabel>
              <Select
                value={selectedLanguage}
                onChange={(event) => setSelectedLanguage(event.target.value)}
              >
                {LANGUAGES.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<MdDownload />}
                variant="outline"
                colorScheme="blue"
                onClick={handleDownload}
                isLoading={isFetchingDownload}
              >
                {t('admin.click_to_download_purchase_terms', {
                  fallback: 'Click to download Purchase Terms',
                })}
              </Button>
              <Text fontSize="sm" color="gray.500">
                {t('admin.current_language_label', {
                  fallback: 'Current language: {{language}}',
                  language: selectedLanguageLabel,
                })}
              </Text>
            </HStack>

            <DocumentUploader
              entityType="user"
              entityId={entityId}
              docType="purchase_terms"
              accept=".pdf"
              onUploaded={handleUploaded}
            />

            <DocumentList
              entityType="user"
              entityId={entityId}
              docType="purchase_terms"
              canDelete
              resolveRole={false}
              currentRole="admin"
              refreshTrigger={refreshTrigger}
            />
          </Stack>
        </Card>
      </Stack>
    </PageShell>
  );
};

export default AdminPurchaseTermsPage;
